import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { type SearchInfoDetailData } from '@/app/search/types.ts';
import { getSearchModeFromSearchParams } from '@/app/components/search/utils/query2.ts';
import type { DefaultDestItems } from '@/app/components/search/utils';
import { getTotalNumAdults, getTotalNumChildren, getTotalNumInfants } from '@/utils/peopleUtils.ts';
import { createDatesParams, createDestinationParam } from '@/app/common/memsearch/getSearchParams.ts';
import logDevOnly from '../utils/logDevOnly';
import { type Actions, type Categories, registerEvents, getDuration, getOriginData } from './utils';
import convertDateFrom from './utils/convertDateFrom';
import getDestinationData from './utils/getDestinationData';

type AnalyticsUserInteractionSearch = {
  action: Actions | string;
  category: Categories | string;
  defaultDestItems?: DefaultDestItems[];
  isSearch?: boolean;
  label: string;
  searchParams: SearchParams;
  searchInfoDetailData: SearchInfoDetailData;
};

export const analyticsUserInteraction = async ({
  action,
  label,
  category,
  isSearch,
  defaultDestItems,
  searchParams,
  searchInfoDetailData,
}: AnalyticsUserInteractionSearch) => {
  let search = {};

  if (isSearch) {
    const adddays = searchParams[URLParameters.ADD_DAYS];
    const leisure = searchParams[URLParameters.LEISURE]; // Same param name - check this
    const leisureType = searchParams[URLParameters.LEISURE]; // Same param name - check this
    const dateFrom = searchParams[URLParameters.DATE_FROM];
    const dateTo = searchParams[URLParameters.DATE_TO];
    const searchMode = getSearchModeFromSearchParams(searchParams);
    const adults = searchParams[URLParameters.ADULTS];
    const ages = searchParams[URLParameters.AGES];
    const destination = searchParams[URLParameters.DESTINATION];
    const addDaysNumber = Number.isNaN(Number(adddays)) ? 0 : Number(adddays);
    const datesParams = createDatesParams(addDaysNumber, dateFrom, dateTo);
    const { datefrom: searchDateFrom, dateto: searchDateTo } = datesParams;
    const destinationParams = createDestinationParam({ leisureType, leisure, destination });
    const { destination: searchDestination } = destinationParams;
    const destinationData = await getDestinationData({
      destination: searchParams[URLParameters.DESTINATION] || searchDestination,
      defaultDestItems: defaultDestItems || [],
      isSearch,
      searchMode,
      searchInfoDetailData,
    });
    const originData = searchParams[URLParameters.ORIGIN] ? await getOriginData({ origin, searchInfoDetailData }) : {};
    const duration = getDuration({ dateFrom: searchDateFrom, dateTo: searchDateTo });
    const totalNumAdults = getTotalNumAdults(adults);
    const totalNumChildren = getTotalNumChildren(ages);
    const totalNumInfants = getTotalNumInfants(ages);
    const numberOfGuests = totalNumAdults + totalNumChildren + totalNumInfants;
    const departureDate = searchDateFrom === 'A' ? 'Any' : convertDateFrom(searchParams[URLParameters.DATE_FROM]);
    const returnDate =
      searchDateTo.indexOf('T') !== -1 || searchDateTo.indexOf('W') !== -1 ? 'Any' : searchDateTo.replace(/-/g, '');

    search = {
      search: {
        ...(searchParams[URLParameters.ORIGIN] ? originData : {}),
        ...destinationData,
        departureDate,
        duration,
        quantity: '1',
        numberOfGuests,
        adults: totalNumAdults,
        children: totalNumChildren,
        infants: totalNumInfants,
        returnDate,
      },
    };
  }

  const data = {
    event: 'userInteraction',
    user: {
      interaction: {
        category,
        action,
        label,
        n: '0',
      },
    },
    ...search,
  };

  logDevOnly({ data, title: 'ANALYTICS INTERACTION EVENT' });

  registerEvents(data);
};
